import { useRef, useState, useEffect } from 'react';

import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import posthog from 'posthog-js';
import { ToastContainer } from 'react-toastify';

import { Features } from '@lessonPlanner/constants';
import { OrganizationSource } from '@lessonPlanner/enums';

import AdBlockDetected from '../components/AdBlockDetected';
import CreditCountNav from '../components/CreditCountNav';
import Loader from '../components/Loader';
import MobileSidebar from '../components/MobileSidebar';
import Sidebar from '../components/Sidebar';
import { routes } from '../constants/routes';
import { useIsAdBlocked } from '../hooks/useIsAdBlocked';
import { useSidebarState } from '../hooks/useSidebarState';
import DisabledFeaturePage from '../pages/disabled-feature';
import LoginRequiredPage from '../pages/login-required';
import { useHasAllowedOrganizations } from '../utils/abac';

interface Props {
  children: React.ReactNode;
  featureFlag?: Features;
  allowedSources?: OrganizationSource[];
  isLoginRequired?: boolean;
  redirectToHome?: boolean;
}

export default function SidebarLayout({
  children,
  allowedSources,
  featureFlag,
  isLoginRequired,
  redirectToHome,
}: Props) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { sidebarCollapsed, setSidebarCollapsed } = useSidebarState();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const router = useRouter();
  const { status } = useSession();
  const isAdBlocked = useIsAdBlocked();
  const hasAllowedSources = useHasAllowedOrganizations(allowedSources);
  const hasAccessToPage = useHasAllowedOrganizations(allowedSources);
  const authenticated = status === 'authenticated';
  const isNotLoggedIn = isLoginRequired && !authenticated;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted && redirectToHome && (!hasAllowedSources || isNotLoggedIn)) {
      router.push(routes.MAIN);
    }
  }, [isMounted, redirectToHome, hasAllowedSources, isNotLoggedIn, router]);

  if (!isMounted) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        <Loader position="relative" />
      </div>
    );
  }

  const canAccessFeature =
    !featureFlag || (featureFlag && posthog.isFeatureEnabled(featureFlag));

  if (isNotLoggedIn) {
    return <LoginRequiredPage />;
  }

  if (!canAccessFeature || !hasAllowedSources) {
    if (isAdBlocked) {
      return <AdBlockDetected featureFlag={featureFlag} />;
    }
    return <DisabledFeaturePage />;
  }

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const renderSidebar = () => {
    return (
      <div
        className={clsx(
          'relative hidden border-r border-black/10 transition-all duration-500 ease-in-out md:block',
          !sidebarCollapsed ? 'w-80' : 'w-0',
        )}
      >
        {!sidebarCollapsed && <Sidebar />}
      </div>
    );
  };

  return (
    <div className="h-screen flex-col overflow-hidden">
      <CreditCountNav
        sidebarCollapsed={sidebarCollapsed}
        toggleMobileSidebar={toggleMobileSidebar}
        toggleSidebar={toggleSidebar}
      />
      <MobileSidebar
        isOpen={isMobileSidebarOpen}
        setIsOpen={setIsMobileSidebarOpen}
      />
      <div className="flex h-[calc(100vh-76px)] w-full overflow-hidden">
        {renderSidebar()}
        <div
          ref={scrollContainerRef}
          className="flex h-full min-w-0 flex-1 flex-col overflow-y-auto"
        >
          <div className="h-full flex-1">
            {canAccessFeature && hasAccessToPage ? (
              children
            ) : (
              <DisabledFeaturePage />
            )}
          </div>
        </div>
      </div>
      <ToastContainer
        enableMultiContainer
        hideProgressBar
        className="mt-44 sm:mt-24 xl:mt-14"
        containerId="dialogToastContainer"
      />
    </div>
  );
}
